import React, { useState, useEffect } from 'react';
import { Lead, LeadStatus } from '../../types/lead.types';
import { updateLead } from '../../services/lead.service';
import { toast } from 'react-hot-toast';
import {
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { doc, getDoc, Timestamp, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db, storage } from '../../config/firebase';
import { getDownloadURL, ref } from 'firebase/storage';
import { useAuth } from '../../contexts/AuthContext';
import { Appointment } from '../../types/schedule.types';

import LeadInfoTab from './detail/LeadInfoTab';
import LeadScheduleTab from './detail/LeadScheduleTab';
import LeadDocumentsTab from './detail/LeadDocumentsTab';
import LeadContactsTab from './detail/LeadContactsTab';
import LeadDeterminationTab from './detail/LeadDeterminationTab';
import LeadSignatureTab from './detail/LeadSignatureTab';

interface LeadDetailModalProps {
  lead: Lead;
  onClose: () => void;
  onUpdate: (lead: Lead) => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  canNavigate?: {
    prev: boolean;
    next: boolean;
  };
  validationDeadlineDays?: number;
}

const statusOptions = [
  { value: 'uncontacted', label: 'Uncontacted', color: 'bg-gray-100 text-gray-800' },
  { value: 'cold', label: 'Cold', color: 'bg-blue-100 text-blue-800' },
  { value: 'warm', label: 'Warm', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'hot', label: 'Hot', color: 'bg-orange-100 text-orange-800' },
  { value: 'enrolled', label: 'Enrolled', color: 'bg-green-100 text-green-800' },
  { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-800' },
  { value: 'dnc', label: 'DNC', color: 'bg-gray-100 text-gray-800' }
];

const LeadDetailModal: React.FC<LeadDetailModalProps> = ({
  lead,
  onClose,
  onUpdate,
  onNavigate,
  canNavigate = { prev: false, next: false },
  validationDeadlineDays
}) => {
  const { currentUser, userProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editedLead, setEditedLead] = useState(lead);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'documents' | 'contacts' | 'determination' | 'schedule' | 'signature'>('info');
  const [capturedByName, setCapturedByName] = useState<string>('');

  // Scheduling state
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);

  const [documents, setDocuments] = useState<{ label: string; url: string }[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(true);

  // Document upload state
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setEditedLead(lead);
    setIsEditing(false);
    fetchCapturedByName();
    loadDocuments();
    loadAppointments();
  }, [lead]);

  // Load appointments for this lead
  const loadAppointments = async () => {
    if (!lead.id) return;

    setLoadingAppointments(true);
    try {
      const q = query(
        collection(db, 'appointments'),
        where('leadId', '==', lead.id),
        orderBy('date', 'desc')
      );
      const snapshot = await getDocs(q);
      const appts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Appointment));
      setAppointments(appts);
    } catch (error) {
      console.error('Error loading appointments:', error);
    } finally {
      setLoadingAppointments(false);
    }
  };

  const fetchCapturedByName = async () => {
    if (lead.capturedByName) {
      setCapturedByName(lead.capturedByName);
    } else if (lead.capturedBy || lead.createdBy) {
      try {
        const userId = lead.capturedBy || lead.createdBy;
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const name = userData.name || 'Unknown User';
          setCapturedByName(name);
          // Update lead if name was missing
          if (!lead.capturedByName) {
            const updated = { ...lead, capturedByName: name };
            await updateLead(lead.id, { capturedByName: name });
            onUpdate(updated);
          }
        } else {
          setCapturedByName('Unknown User');
        }
      } catch (error) {
        console.error('Error fetching user name:', error);
        setCapturedByName('Unknown User');
      }
    }
  };

  const loadDocuments = async () => {
    setLoadingDocuments(true);
    try {
      const docList: { label: string; url: string }[] = [];

      if (lead.documents) {
        // Check for direct URL fields (new format)
        if (lead.documents.interestForm && typeof lead.documents.interestForm === 'string' && lead.documents.interestForm.length > 0) {
          docList.push({ label: 'Interest Form', url: lead.documents.interestForm });
        }
        if (lead.documents.confirmationText && typeof lead.documents.confirmationText === 'string' && lead.documents.confirmationText.length > 0) {
          docList.push({ label: 'Confirmation/Screenshot', url: lead.documents.confirmationText });
        }
        if (lead.documents.misc && typeof lead.documents.misc === 'string' && lead.documents.misc.length > 0) {
          docList.push({ label: 'Other Document', url: lead.documents.misc });
        }

        // Check for array fields (proofOfIncome, other)
        if (lead.documents.proofOfIncome && Array.isArray(lead.documents.proofOfIncome)) {
          lead.documents.proofOfIncome.forEach((url, index) => {
            if (url && typeof url === 'string' && url.length > 0) {
              docList.push({ label: `Proof of Income ${index + 1}`, url });
            }
          });
        }
        if (lead.documents.other && Array.isArray(lead.documents.other)) {
          lead.documents.other.forEach((url, index) => {
            if (url && typeof url === 'string' && url.length > 0) {
              docList.push({ label: `Additional Document ${index + 1}`, url });
            }
          });
        }

        // Also check legacy ids array (storage paths that need resolving)
        if (lead.documents.ids && Array.isArray(lead.documents.ids)) {
          for (const docPath of lead.documents.ids) {
            try {
              // Check if it's already a URL or a storage path
              if (docPath.startsWith('http')) {
                docList.push({ label: 'Document', url: docPath });
              } else {
                const storageRef = ref(storage, docPath);
                const url = await getDownloadURL(storageRef);
                docList.push({ label: 'Document', url });
              }
            } catch (err) {
              console.error('Error loading document from path:', docPath, err);
            }
          }
        }
      }

      setDocuments(docList);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only update the editable fields, filtering out undefined values
      const updates: Record<string, any> = {
        parentName: editedLead.parentName || '',
        email: editedLead.email || '',
        phone: editedLead.phone || '',
        address: editedLead.address || '',
        city: editedLead.city || '',
        state: editedLead.state || '',
        zipCode: editedLead.zipCode || '',
        source: editedLead.source || '',
        notes: editedLead.notes || '',
        // Persist children edits (e.g. removing a duplicate child) and campus.
        children: (editedLead.children || []).map((c) =>
          Object.fromEntries(Object.entries(c).filter(([, v]) => v !== undefined))
        ),
        schoolSegmentId: editedLead.schoolSegmentId || '',
        schoolSegmentName: editedLead.schoolSegmentName || '',
        rideProvided: editedLead.rideProvided || false,
        lastUpdated: Timestamp.now()
      };

      await updateLead(editedLead.id, updates, currentUser?.uid, userProfile?.name);
      toast.success('Lead updated successfully');
      setIsEditing(false);
      onUpdate({ ...editedLead, ...updates });
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: LeadStatus) => {
    const updatedLead = { ...editedLead, status: newStatus };
    setEditedLead(updatedLead);

    // Auto-save status changes
    setSaving(true);
    try {
      await updateLead(lead.id, { status: newStatus });
      toast.success('Status updated');
      onUpdate(updatedLead);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const currentStatusOption = statusOptions.find(opt => opt.value === editedLead.status) || statusOptions[0];

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-stretch sm:items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white sm:rounded-lg shadow-xl max-w-4xl w-full h-full sm:h-auto sm:max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex flex-wrap justify-between items-center gap-2 p-3 sm:p-6 border-b">
          <div className="flex items-center gap-2 sm:gap-4">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 whitespace-nowrap">Lead Details</h2>
            {onNavigate && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onNavigate('prev')}
                  disabled={!canNavigate.prev}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => onNavigate('next')}
                  disabled={!canNavigate.next}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Status Nudge — desktop only */}
            {editedLead.status === 'uncontacted' && (
              <span className="hidden sm:inline text-xs font-semibold text-orange-600 animate-bounce-right whitespace-nowrap">
                Update this &rarr;
              </span>
            )}
            {/* Status Dropdown */}
            <select
              value={editedLead.status}
              onChange={(e) => handleStatusChange(e.target.value as LeadStatus)}
              className={`px-3 py-1 rounded-full text-sm font-medium ${currentStatusOption.color}`}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                Edit
              </button>
            )}
            {isEditing && (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setEditedLead(lead);
                    setIsEditing(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-md"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Tabs — horizontally scrollable on narrow screens so every tab is reachable */}
        <div className="border-b">
          <nav
            className="flex gap-4 sm:gap-8 px-3 sm:px-6 overflow-x-auto scrollbar-hide"
            aria-label="Tabs"
          >
            {['info', 'schedule', 'documents', 'contacts', 'determination', ...(lead.signature ? ['signature'] : [])].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`py-3 px-1 border-b-2 font-medium text-sm capitalize whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Content — fills remaining flex space instead of a magic-number maxHeight */}
        <div className="p-3 sm:p-6 overflow-y-auto flex-1 min-h-0">
          {activeTab === 'info' && (
            <LeadInfoTab
              editedLead={editedLead}
              setEditedLead={setEditedLead}
              isEditing={isEditing}
              capturedByName={capturedByName}
            />
          )}

          {activeTab === 'schedule' && (
            <LeadScheduleTab
              lead={lead}
              editedLead={editedLead}
              appointments={appointments}
              loadingAppointments={loadingAppointments}
              onAppointmentsChange={loadAppointments}
            />
          )}

          {activeTab === 'documents' && (
            <LeadDocumentsTab
              lead={lead}
              editedLead={editedLead}
              setEditedLead={setEditedLead}
              onUpdate={onUpdate}
              documents={documents}
              loadingDocuments={loadingDocuments}
              uploading={uploading}
              setUploading={setUploading}
              onDocumentsReload={loadDocuments}
            />
          )}

          {activeTab === 'contacts' && (
            <LeadContactsTab
              lead={lead}
              editedLead={editedLead}
              setEditedLead={setEditedLead}
              onUpdate={onUpdate}
              capturedByName={capturedByName}
            />
          )}

          {activeTab === 'signature' && (
            <LeadSignatureTab lead={lead} capturedByName={capturedByName} />
          )}

          {activeTab === 'determination' && (
            <LeadDeterminationTab
              lead={lead}
              editedLead={editedLead}
              setEditedLead={setEditedLead}
              onUpdate={onUpdate}
              saving={saving}
              setSaving={setSaving}
              validationDeadlineDays={validationDeadlineDays}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadDetailModal;
